import React, { useState, useEffect } from 'react';
import { backupAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import './Backups.css';

const Backups = () => {
  const { formatFechaHora } = useZonaHoraria();
  const [listado, setListado] = useState([]);
  const [estadoAuto, setEstadoAuto] = useState(null);
  const [directorioBackups, setDirectorioBackups] = useState('');
  const [loading, setLoading] = useState(true);
  const [creando, setCreando] = useState(false);
  const [restaurando, setRestaurando] = useState(null);
  const [configDir, setConfigDir] = useState(null);
  const [rutaEdit, setRutaEdit] = useState('');
  const [validandoDir, setValidandoDir] = useState(false);
  const [guardandoDir, setGuardandoDir] = useState(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    loadListado();
    if (hasPermission('configurar_seguridad_backup') || hasPermission('ver_backup')) {
      backupAPI.getConfigDirectorio()
        .then((res) => {
          if (res.success && res.data) {
            setConfigDir(res.data);
            setRutaEdit(res.data.backup_dir_local || res.data.directorio_activo || '');
          }
        })
        .catch(() => {});
    }
  }, [hasPermission]);

  const loadListado = async () => {
    try {
      setLoading(true);
      const [resList, resEstado] = await Promise.all([
        backupAPI.listar(),
        backupAPI.estado().catch(() => null),
      ]);
      if (resList.success && resList.data) {
        setListado(resList.data);
      } else {
        setListado([]);
      }
      if (resEstado?.success && resEstado.data) {
        setEstadoAuto(resEstado.data);
        if (resEstado.data.directorio) setDirectorioBackups(resEstado.data.directorio);
      } else if (resList.meta) {
        setEstadoAuto(resList.meta);
        if (resList.meta.directorio) setDirectorioBackups(resList.meta.directorio);
      }
      if (resList.directorio) setDirectorioBackups(resList.directorio);
    } catch (error) {
      toast.error('Error al listar backups');
      setListado([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEjecutar = async () => {
    if (!hasPermission('ejecutar_backup')) {
      toast.error('No tiene permiso para crear backups');
      return;
    }
    if (!window.confirm('¿Crear un nuevo backup de la base de datos ahora?')) return;
    try {
      setCreando(true);
      const res = await backupAPI.ejecutar();
      if (res.success) {
        toast.success(res.message || 'Backup creado correctamente');
        loadListado();
      } else {
        toast.error(res.message || 'Error al crear backup');
      }
    } catch (error) {
      toast.error(error.message || 'Error al crear backup');
    } finally {
      setCreando(false);
    }
  };

  const handleRestaurar = async (nombre) => {
    if (!hasPermission('restaurar_backup')) {
      toast.error('No tiene permiso para restaurar backups');
      return;
    }
    if (
      !window.confirm(
        `¿Restaurar la base de datos desde "${nombre}"?\n\nSe borrarán todas las tablas actuales, se importará el backup y se aplicarán las migraciones de la versión en ejecución (p. ej. esquema 4.0.0 sobre un backup 3.0.1).`
      )
    ) {
      return;
    }
    try {
      setRestaurando(nombre);
      const res = await backupAPI.restaurar(nombre);
      if (res.success) {
        toast.success(res.message || 'Base de datos restaurada. Recargue la aplicación.');
        loadListado();
      } else {
        toast.error(res.message || 'Error al restaurar');
      }
    } catch (error) {
      toast.error(error.message || 'Error al restaurar backup');
    } finally {
      setRestaurando(null);
    }
  };

  const handleEliminar = async (nombre) => {
    if (!hasPermission('eliminar_backup')) {
      toast.error('No tiene permiso para eliminar backups');
      return;
    }
    if (!window.confirm(`¿Eliminar el archivo de backup "${nombre}"?`)) return;
    try {
      const res = await backupAPI.eliminar(nombre);
      if (res.success) {
        toast.success('Backup eliminado');
        loadListado();
      } else {
        toast.error(res.message || 'Error al eliminar');
      }
    } catch (error) {
      toast.error(error.message || 'Error al eliminar');
    }
  };

  const formatSize = (bytes) => {
    if (bytes == null) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatFecha = (f) => {
    if (!f) return '-';
    return formatFechaHora(f);
  };

  const handleValidarDirectorio = async () => {
    if (!rutaEdit.trim()) return toast.warning('Ingrese una ruta');
    setValidandoDir(true);
    try {
      const res = await backupAPI.validarDirectorio(rutaEdit.trim());
      if (res.success) {
        toast.success(res.message || 'Ruta válida');
      } else {
        toast.error(res.message || 'Ruta inválida');
      }
    } catch (e) {
      toast.error(e.message || 'Error al validar');
    } finally {
      setValidandoDir(false);
    }
  };

  const handleGuardarDirectorio = async () => {
    if (!rutaEdit.trim()) return toast.warning('Ingrese una ruta');
    setGuardandoDir(true);
    try {
      const res = await backupAPI.saveConfigDirectorio(rutaEdit.trim());
      if (res.success) {
        toast.success(res.message || 'Carpeta actualizada');
        const cfg = await backupAPI.getConfigDirectorio();
        if (cfg.success && cfg.data) {
          setConfigDir(cfg.data);
          setDirectorioBackups(cfg.data.directorio_activo || rutaEdit.trim());
        }
        loadListado();
      } else {
        toast.error(res.message || 'No se pudo guardar');
      }
    } catch (e) {
      toast.error(e.message || 'Error al guardar');
    } finally {
      setGuardandoDir(false);
    }
  };

  return (
    <div className="backups-page">
      <div className="page-header">
        <h1>Backups de base de datos</h1>
        {hasPermission('ejecutar_backup') && (
          <button
            type="button"
            className="btn-primary"
            onClick={handleEjecutar}
            disabled={creando}
          >
            {creando ? 'Creando...' : 'Crear backup ahora'}
          </button>
        )}
      </div>

      <p className="backups-desc">
        Los backups se guardan en el servidor. Puede crear uno manual, restaurar desde un archivo existente o eliminar archivos que ya no necesite.
        {directorioBackups && (
          <span className="backups-directorio" style={{ display: 'block', marginTop: 8, fontSize: '0.9rem', color: '#555' }}>
            <strong>Directorio en servidor:</strong> <code style={{ fontSize: '0.85rem' }}>{directorioBackups}</code>
          </span>
        )}
      </p>

      {hasPermission('configurar_seguridad_backup') && configDir && (
        <div className="backups-seguridad-dir" style={{
          marginBottom: 16,
          padding: '14px 16px',
          background: '#fff8e6',
          border: '1px solid #f0d78c',
          borderRadius: 8,
        }}>
          <strong>Seguridad — carpeta de backups</strong>
          <p style={{ margin: '8px 0 12px', fontSize: '0.9rem', color: '#555' }}>
            Directorio activo: <code>{configDir.directorio_activo}</code>
            {' · '}
            Origen: {configDir.origen}
            {configDir.backup_dir_env && (
              <span> · BACKUP_DIR en .env: <code>{configDir.backup_dir_env}</code></span>
            )}
          </p>
          {!configDir.editable ? (
            <p style={{ color: '#856404', fontSize: '0.9rem' }}>
              La carpeta está fijada por la variable <code>BACKUP_DIR</code> en el servidor y no puede editarse desde aquí.
            </p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="text"
                  value={rutaEdit}
                  onChange={(e) => setRutaEdit(e.target.value)}
                  placeholder="Ruta absoluta de la carpeta de backups"
                  style={{ flex: '1 1 280px', padding: '8px 10px', fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
                <button type="button" className="btn-secondary" onClick={handleValidarDirectorio} disabled={validandoDir}>
                  {validandoDir ? 'Validando…' : 'Validar'}
                </button>
                <button type="button" className="btn-primary" onClick={handleGuardarDirectorio} disabled={guardandoDir}>
                  {guardandoDir ? 'Guardando…' : 'Guardar carpeta'}
                </button>
              </div>
              <small style={{ display: 'block', marginTop: 8, color: '#666' }}>
                La ruta debe existir y ser escribible por el proceso del servidor.
              </small>
            </>
          )}
        </div>
      )}

      {estadoAuto && (
        <div className="backups-estado-auto" style={{
          marginBottom: 16,
          padding: '12px 16px',
          background: '#f0f4ff',
          border: '1px solid #c5d4f5',
          borderRadius: 8,
          fontSize: '0.95rem',
        }}>
          <strong>Backup automático (20:00 AR)</strong>
          <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
            <span>Estado: {estadoAuto.backup_auto_enabled ? 'Activo' : 'Deshabilitado (BACKUP_AUTO=false)'}</span>
            {estadoAuto.proxima_ejecucion && (
              <span>Próxima ejecución: {formatFecha(estadoAuto.proxima_ejecucion)}</span>
            )}
            {estadoAuto.last_auto && (
              <span>Último automático: {formatFecha(estadoAuto.last_auto.fecha || estadoAuto.last_auto)} — {estadoAuto.last_auto.nombre || ''}</span>
            )}
            {estadoAuto.last_manual && (
              <span>Último manual: {formatFecha(estadoAuto.last_manual.fecha || estadoAuto.last_manual)} — {estadoAuto.last_manual.nombre || ''}</span>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Archivo</th>
                <th>Tipo</th>
                <th>Tamaño</th>
                <th>Fecha</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {listado.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">No hay backups disponibles</td>
                </tr>
              ) : (
                listado.map((item) => (
                  <tr key={item.nombre}>
                    <td>{item.nombre}</td>
                    <td>{item.automatico ? 'Automático' : 'Manual'}</td>
                    <td>{formatSize(item.tamaño ?? item.size)}</td>
                    <td>{formatFecha(item.fecha)}</td>
                    <td>
                      <div className="action-buttons">
                        {hasPermission('restaurar_backup') && (
                          <button
                            type="button"
                            className="btn-edit"
                            onClick={() => handleRestaurar(item.nombre)}
                            disabled={restaurando !== null}
                          >
                            {restaurando === item.nombre ? 'Restaurando...' : 'Restaurar'}
                          </button>
                        )}
                        {hasPermission('eliminar_backup') && (
                          <button
                            type="button"
                            className="btn-delete"
                            onClick={() => handleEliminar(item.nombre)}
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Backups;
